import React, { useEffect, useRef, useState } from 'react'
import { AiOutlineCloseCircle } from 'react-icons/ai'
import { FiCamera } from 'react-icons/fi'

const stopStream = (stream) => {
  stream?.getTracks().forEach((track) => track.stop())
}

const CameraCaptureModal = ({
  isOpen,
  onClose,
  onCapture,
  facingMode = 'environment',
  title = 'Take a picture',
}) => {
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen) return undefined

    let active = true
    setLoading(true)
    setError('')

    const startCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        if (active) {
          setError('Camera is not supported on this device. Please select from gallery instead.')
          setLoading(false)
        }
        return
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        })

        if (!active) {
          stopStream(stream)
          return
        }

        streamRef.current = stream
        const video = videoRef.current
        if (video) {
          video.srcObject = stream
          await video.play()
        }
        setLoading(false)
      } catch (cameraError) {
        if (!active) return
        setError('Unable to open the camera. Check permissions or choose from gallery instead.')
        setLoading(false)
      }
    }

    startCamera()

    return () => {
      active = false
      stopStream(streamRef.current)
      streamRef.current = null
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }
  }, [isOpen, facingMode])

  const handleCapture = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth || !video.videoHeight) return

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const context = canvas.getContext('2d')
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    canvas.toBlob(
      (blob) => {
        if (!blob) return
        const file = new File([blob], `photo-${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        })
        onCapture(file)
      },
      'image/jpeg',
      0.92
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black z-[100000] flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 bg-black/80 text-white">
        <h3 className="text-base font-semibold">{title}</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-white/80 hover:text-white"
          aria-label="Close camera"
        >
          <AiOutlineCloseCircle className="h-7 w-7" />
        </button>
      </div>

      <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden">
        {loading && (
          <p className="text-white text-sm">Starting camera...</p>
        )}

        {error && (
          <div className="px-6 text-center">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${loading || error ? 'hidden' : 'block'}`}
        />
      </div>

      <div className="px-4 py-5 bg-black/80 flex justify-center">
        <button
          type="button"
          onClick={handleCapture}
          disabled={loading || Boolean(error)}
          className="w-16 h-16 rounded-full bg-white flex items-center justify-center disabled:opacity-40"
          aria-label="Capture photo"
        >
          <FiCamera className="text-black" size={28} />
        </button>
      </div>
    </div>
  )
}

export default CameraCaptureModal
